import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Bell, ArrowRight, Loader2 } from "lucide-react";
import { ContentWithImages } from "@/components/ImageTextarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegistrationNoticePage() {
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotice();
  }, []);

  const fetchNotice = async () => {
    try {
      const response = await axios.get(`${API}/academy/registration-notice`);
      const data = response.data;
      
      // If no notice or not active, redirect directly to registration
      if (!data || !data.is_active) {
        navigate("/academy/register", { replace: true });
        return;
      }
      
      setNotice(data);
    } catch (error) {
      console.error("Error fetching notice:", error);
      // On error, proceed to registration
      navigate("/academy/register", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    // Store that user has seen the notice
    sessionStorage.setItem("registration_notice_seen", "true");
    navigate("/academy/register");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!notice) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/academy">
              <Button variant="ghost" className="text-white hover:text-blue-200">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Academy
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white uppercase" style={{ fontFamily: "'Times New Roman', Times, Georgia, serif", letterSpacing: "0.05em" }}>🎓 SCC Football Academy</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <Card className="p-8 bg-slate-900/50 shadow-xl">
          {/* Icon and Title */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500/20 rounded-full mb-4">
              <Bell className="w-8 h-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold text-white">
              {notice.title || "Important Notice"}
            </h2>
          </div>

          {/* Notice Content */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-6 mb-8">
            <ContentWithImages 
              content={notice.content || "Please review this notice before proceeding."} 
              className="leading-relaxed text-slate-200"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/academy">
              <Button variant="outline" className="w-full sm:w-auto">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            </Link>
            <Button 
              onClick={handleContinue}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
            >
              Continue to Registration
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="mt-6 pt-5 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500 mb-2">Not ready for a full term? Just want to try a single session?</p>
            <Link to="/academy/adhoc-signup" data-testid="adhoc-signup-link">
              <Button variant="outline" className="w-full sm:w-auto border-orange-400 text-orange-600 hover:bg-orange-50">
                Book a single session (pay-as-you-go)
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
